import React, { useState, useEffect, useCallback } from "react";
import AdminLayout from "./AdminLayout";
import axiosUserInstance from "../../axiosUserInstance";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import "../../Assets/css/ReferralCodes.css";

const ReferralCodes = () => {
  const [codes, setCodes] = useState([]);
  const [newCode, setNewCode] = useState("");
  const [newName, setNewName] = useState("");
  const [newExpiry, setNewExpiry] = useState("");
  const [createdCode, setCreatedCode] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState("");

  const fetchCodes = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axiosUserInstance.get("/referral/admin/list");
      if (res.status === 200) {
        setCodes(res.data.data || []);
      }
    } catch (err) {
      toast.error("Failed to load referral codes");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handleCreate = async () => {
    const code = newCode
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "");
    if (!code) {
      toast.error("Please enter a valid code (letters and numbers only)");
      return;
    }
    if (code.length < 6) {
      toast.error("Code must be at least 6 characters long");
      return;
    }
    setIsCreating(true);
    try {
      const res = await axiosUserInstance.post("/referral/admin/create", {
        code,
        name: newName.trim(),
        expiryDate: newExpiry || null,
      });
      if (res.status === 201) {
        toast.success(`Code "${code}" created!`);
        setCreatedCode(code);
        setNewCode("");
        setNewName("");
        setNewExpiry("");
        fetchCodes();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to create code");
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      const res = await axiosUserInstance.put(`/referral/admin/toggle/${id}`);
      if (res.status === 200) {
        toast.success(`Code ${currentStatus ? "disabled" : "enabled"}`);
        fetchCodes();
      }
    } catch (err) {
      toast.error("Failed to toggle code status");
    }
  };

  const handleDelete = async (id, code) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Delete code "${code}"? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await axiosUserInstance.delete(`/referral/admin/${id}`);
          if (res.status === 200) {
            toast.success(`Code "${code}" deleted`);
            fetchCodes();
          }
        } catch (err) {
          toast.error("Failed to delete code");
        }
      }
    });
  };

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(""), 2000);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <AdminLayout>
      <div className="rc-page">
        <div className="rc-header">
          <h2>🎁 Referral Codes</h2>
          <p>Create and manage referral codes for content creators</p>
        </div>

        {/* Create Code Panel */}
        <div className="rc-create-card">
          <h3>Create New Code</h3>
          <div className="rc-create-row">
            <div className="rc-create-field">
              <label>
                Code <span className="rc-required">*</span>
              </label>
              <input
                type="text"
                id="admin-referral-code-input"
                className="rc-input"
                placeholder="e.g. IWBIOME4FR"
                value={newCode}
                onChange={(e) =>
                  setNewCode(
                    e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "")
                  )
                }
                maxLength={20}
              />
              <small>Letters and numbers only</small>
            </div>
            <div className="rc-create-field">
              <label>
                Name / Tag <span className="rc-optional">(Optional)</span>
              </label>
              <input
                type="text"
                id="admin-referral-name-input"
                className="rc-input"
                placeholder="e.g. John's influencer code"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                maxLength={60}
              />
              <small>Tag to identify who this code belongs to</small>
            </div>
            <div className="rc-create-field">
              <label>
                Expires On <span className="rc-optional">(Optional)</span>
              </label>
              <input
                type="date"
                className="rc-input"
                value={newExpiry}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) => setNewExpiry(e.target.value)}
              />
              <small>If empty, defaults to 30 days</small>
            </div>
            <button
              className="rc-create-btn"
              onClick={handleCreate}
              disabled={isCreating || !newCode}
            >
              {isCreating ? "Creating…" : "＋ Create"}
            </button>
          </div>

          {createdCode && (
            <div className="rc-created-banner">
              <span>✅ Code created:</span>
              <strong className="rc-created-code">{createdCode}</strong>
              <button
                className="rc-copy-btn"
                onClick={() => handleCopy(createdCode)}
              >
                {copied === createdCode ? "Copied ✓" : "Copy"}
              </button>
            </div>
          )}
        </div>

        {/* Codes Table */}
        <div className="rc-table-card">
          <div className="rc-table-header">
            <h3>All Codes</h3>
            <span className="rc-count">{codes.length} total</span>
          </div>

          {isLoading ? (
            <div className="rc-loading">Loading codes…</div>
          ) : codes.length === 0 ? (
            <div className="rc-empty">
              <p>No referral codes yet. Create one above!</p>
            </div>
          ) : (
            <div className="rc-table-wrapper">
              <table className="rc-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name / Tag</th>
                    <th>Created</th>
                    <th>Days Left</th>
                    <th>Usage</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {codes.map((c) => (
                    <tr
                      key={c._id}
                      className={!c.isActive ? "rc-row-disabled" : ""}
                    >
                      <td>
                        <div className="rc-code-cell">
                          <span className="rc-code-text">{c.code}</span>
                          <button
                            className="rc-inline-copy"
                            onClick={() => handleCopy(c.code)}
                            title="Copy code"
                          >
                            {copied === c.code ? "✓" : "⎘"}
                          </button>
                        </div>
                      </td>
                      <td className="rc-name-cell">
                        {c.name || <span className="rc-na">—</span>}
                      </td>
                      <td>{formatDate(c.createdAt)}</td>
                      <td>
                        <span
                          className={`rc-badge ${c.daysLeft > 7 ? "rc-badge-green" : c.daysLeft > 0 ? "rc-badge-yellow" : "rc-badge-red"}`}
                        >
                          {c.daysLeft > 0 ? `${c.daysLeft}d` : "Expired"}
                        </span>
                      </td>
                      <td className="rc-usage">{c.usageCount} uses</td>
                      <td>
                        <button
                          className={`rc-toggle ${c.isActive ? "rc-toggle-on" : "rc-toggle-off"}`}
                          onClick={() => handleToggle(c._id, c.isActive)}
                        >
                          {c.isActive ? "Enabled" : "Disabled"}
                        </button>
                      </td>
                      <td>
                        <button
                          className="rc-delete-btn"
                          onClick={() => handleDelete(c._id, c.code)}
                          title="Delete code"
                        >
                          🗑
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default ReferralCodes;
